import { useState } from 'react'
import { Button, Checkbox, LoadingSpinner, Modal, SearchInput } from '@atrvd/saas-ui'
import { Loader2 } from 'lucide-react'
import { formatCurrency } from '@dcp/shared/utils/formatters'
import {
  useAssignAccountFacility,
  useUnassignedConnectionAccounts,
} from '@/hooks/useBankConnections'
import { matchesAccountSearch } from '@/lib/account-search'
import type { FacilityFinancialAccountsScope } from '@/components/financial-accounts/financialAccountsScope'
import { accountsCountLabel } from '@/components/manual-banks/manualBankCopy'
import {
  ACCOUNT_TYPE_LABELS,
  CONNECTION_COPY,
  EM_DASH,
  formatAccountNumber,
} from './connectionManagement.utils'

/** Props for {@link AddAccountsToFacilityDialog}. */
interface AddAccountsToFacilityDialogProps {
  connectionId: string
  /** The connection's display name, as the description names it. */
  institutionName: string
  scope: FacilityFinancialAccountsScope
  onClose: () => void
}

/**
 * "Add account to {Facility}": pick accounts of this connection that have no
 * facility yet and assign them to the facility.
 *
 * Mount it only while open — the unassigned accounts are read on mount, and
 * search and selection start empty each time. The selection is kept by id, so
 * accounts picked before a search narrowed the list stay picked.
 * "Add to facility" is never disabled for validation: with nothing picked it
 * says so and the dialog stays open.
 */
export function AddAccountsToFacilityDialog({
  connectionId,
  institutionName,
  scope,
  onClose,
}: AddAccountsToFacilityDialogProps) {
  const accounts = useUnassignedConnectionAccounts(connectionId)
  const assignFacility = useAssignAccountFacility()
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
  const [showNoneSelected, setShowNoneSelected] = useState(false)

  const rows = accounts.data ?? []
  const visible = rows.filter((account) => matchesAccountSearch(account, search))
  // Only ids still unassigned count — another tab may have assigned one since.
  const pickedIds = rows.filter((account) => selectedIds.has(account.id)).map((account) => account.id)

  const toggle = (id: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
    setShowNoneSelected(false)
  }

  const confirm = () => {
    if (pickedIds.length === 0) {
      setShowNoneSelected(true)
      return
    }
    // The hook toasts the outcome; a failure keeps the dialog open to retry.
    assignFacility.mutate({ ids: pickedIds, facilityId: scope.facilityId }, { onSuccess: onClose })
  }

  const close = () => {
    if (!assignFacility.isPending) onClose()
  }

  const searching = search.trim().length > 0

  return (
    <Modal
      isOpen
      onClose={close}
      title={`Add account to ${scope.facilityName}`}
      description={`Choose ${institutionName} accounts to assign to this facility.`}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">{`${accountsCountLabel(pickedIds.length)} selected`}</span>
          <div className="flex gap-3">
            <Button variant="outline" onClick={close} disabled={assignFacility.isPending}>
              Cancel
            </Button>
            <Button onClick={confirm} disabled={assignFacility.isPending} className="gap-2">
              {assignFacility.isPending && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
              {CONNECTION_COPY.addToFacilityConfirm}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <SearchInput
          aria-label="Search accounts"
          placeholder={CONNECTION_COPY.searchPlaceholder}
          value={search}
          onChange={setSearch}
          clearable
        />
        {showNoneSelected && (
          <p role="alert" className="text-sm text-destructive">
            {CONNECTION_COPY.addToFacilityNoneSelected}
          </p>
        )}
        {accounts.isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : accounts.error ? (
          <p role="alert" className="py-6 text-center text-sm text-destructive">
            {accounts.error.message}
          </p>
        ) : visible.length === 0 ? (
          <div className="py-10 text-center">
            <p className="font-medium">
              {searching ? CONNECTION_COPY.addToFacilityNoMatchTitle : CONNECTION_COPY.addToFacilityEmptyTitle}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {searching
                ? CONNECTION_COPY.addToFacilityNoMatchDescription
                : CONNECTION_COPY.addToFacilityEmptyDescription}
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {visible.map((account) => {
              const checkboxId = `add-to-facility-${account.id}`
              const number = formatAccountNumber(account.account_number)
              return (
                <li key={account.id} className="flex items-center gap-3 rounded-md border p-3">
                  <Checkbox
                    id={checkboxId}
                    checked={selectedIds.has(account.id)}
                    onCheckedChange={(checked) => toggle(account.id, checked === true)}
                  />
                  <label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer">
                    <span className="block truncate font-medium">{account.name}</span>
                    <span className="block text-sm text-muted-foreground">
                      {ACCOUNT_TYPE_LABELS[account.account_type]}
                      {number !== EM_DASH && ` · ${number}`}
                    </span>
                  </label>
                  <span className="shrink-0 text-sm tabular-nums">
                    {account.balance == null ? EM_DASH : formatCurrency(account.balance)}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Modal>
  )
}
